/**
 * Design tokens for the AssistMD dashboard.
 *
 * Centralized color, spacing, shadow, typography, and transition constants.
 * These complement Tailwind classes for programmatic usage.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Colors
// ─────────────────────────────────────────────────────────────────────────────

export const colors = {
  // Primary accent — teal for positive actions, active states
  teal: {
    50: '#edfcfa',
    100: '#d2f7f2',
    200: '#a9efe7',
    300: '#72e2d6',
    400: '#3ecec1',
    light: '#3ecec1',
    500: '#22b5a8',
    DEFAULT: '#22b5a8',
    600: '#189189',
    dark: '#189189',
    700: '#18746f',
    800: '#185c59',
    900: '#184d4a',
    950: '#072f2e',
  },

  // Secondary accent — cyan for informational states
  cyan: {
    50: '#ecfeff',
    light: '#67e8f9',
    DEFAULT: '#06b6d4',
    dark: '#0e7490',
    900: '#164e63',
  },

  // Warning — amber for warnings, attention
  amber: {
    50: '#fffbeb',
    light: '#fcd34d',
    DEFAULT: '#f59e0b',
    dark: '#b45309',
    900: '#78350f',
  },

  // Navy — for dark text, chart elements
  navy: {
    700: '#2a394f',
    800: '#1c2737',
    900: '#111827',
    950: '#0a0f18',
  },

  // Status colors
  status: {
    recording: '#ef4444',   // red-500
    draft: '#94a3b8',       // slate-400
    finalizing: '#f59e0b',  // amber-500
    closed: '#64748b',      // slate-500
    connected: '#22c55e',   // green-500
    disconnected: '#ef4444', // red-500
    reconnecting: '#f59e0b', // amber-500
  },

  // Consent states
  consent: {
    unknown: '#94a3b8',
    granted: '#22c55e',
    revoked: '#ef4444',
    expired: '#78350f',
  },

  // Alert severity
  severity: {
    critical: '#ef4444',
    warning: '#f59e0b',
    info: '#06b6d4',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Gradients
// ─────────────────────────────────────────────────────────────────────────────

export const gradients = {
  /** Primary accent gradient — buttons, active indicators */
  primary: 'linear-gradient(135deg, #22b5a8 0%, #189189 100%)',
  /** Subtle background gradient — page backgrounds */
  background: 'linear-gradient(160deg, #f5f7fa 0%, #edf2f8 40%, #e7f0f2 70%, #f0f4f7 100%)',
  /** Ambient teal wash — subtle header accent */
  ambientTeal: 'radial-gradient(ellipse 80% 50% at 20% 0%, rgba(15,118,110,0.03) 0%, transparent 50%)',
  /** Shimmer sweep for skeleton loaders */
  shimmer: 'linear-gradient(90deg, rgb(226 232 240 / 0.6) 0%, rgb(241 245 249 / 0.9) 50%, rgb(226 232 240 / 0.6) 100%)',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Typography
// ─────────────────────────────────────────────────────────────────────────────

export const typography = {
  /** Display heading — hero sections, large page titles */
  display: { size: '2.25rem', lineHeight: '2.5rem', weight: 700, tracking: '-0.025em' },
  /** H1 — page titles */
  h1: { size: '1.5rem', lineHeight: '2rem', weight: 700, tracking: '-0.02em' },
  /** H2 — section headings */
  h2: { size: '1.25rem', lineHeight: '1.75rem', weight: 600, tracking: '-0.01em' },
  /** H3 — card headings */
  h3: { size: '1rem', lineHeight: '1.5rem', weight: 600, tracking: '0' },
  /** Body — default paragraph text */
  body: { size: '0.875rem', lineHeight: '1.25rem', weight: 400, tracking: '0' },
  /** Body small — secondary descriptions */
  bodySmall: { size: '0.8125rem', lineHeight: '1.125rem', weight: 400, tracking: '0' },
  /** Caption — timestamps, metadata */
  caption: { size: '0.75rem', lineHeight: '1rem', weight: 400, tracking: '0' },
  /** Overline — section labels, uppercase accents */
  overline: { size: '0.6875rem', lineHeight: '1rem', weight: 600, tracking: '0.08em' },
  /** Mono — IDs, codes, technical values */
  mono: { size: '0.8125rem', lineHeight: '1.25rem', weight: 400, tracking: '0' },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Spacing
// ─────────────────────────────────────────────────────────────────────────────

export const spacing = {
  /** Panel header height */
  headerHeight: '2.75rem',    // 44px
  /** Maximum page content width (fills side panel) */
  pageMaxWidth: '100%',
  /** Page horizontal padding */
  pagePadding: '1rem',        // 16px
  /** Card internal padding */
  cardPadding: '1.25rem',     // 20px
  /** Gap between cards in a list */
  cardGap: '0.75rem',         // 12px
  /** Section vertical gap */
  sectionGap: '2rem',         // 32px
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Border Radius
// ─────────────────────────────────────────────────────────────────────────────

export const radii = {
  /** Small elements — badges, dots */
  sm: '0.375rem',
  /** Default — inputs, buttons */
  md: '0.5rem',
  /** Cards, panels */
  lg: '0.75rem',
  /** Large panels, modals */
  xl: '1rem',
  /** Full — pills, avatars */
  full: '9999px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Z-Index Scale
// ─────────────────────────────────────────────────────────────────────────────

export const zIndex = {
  /** Below default (background decorations) */
  behind: -1,
  /** Default stacking context */
  base: 0,
  /** Slightly raised elements (cards on hover) */
  raised: 10,
  /** Sticky headers, navigation bars */
  sticky: 20,
  /** Dropdowns, popovers */
  dropdown: 30,
  /** Overlay backdrops */
  overlay: 40,
  /** Modal dialogs */
  modal: 50,
  /** Toast notifications */
  toast: 60,
  /** Tooltip layer */
  tooltip: 70,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Transitions
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Shadows
// ─────────────────────────────────────────────────────────────────────────────

export const shadows = {
  /** Minimal — subtle depth for flat surfaces */
  xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
  /** Default card shadow */
  sm: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
  /** Hovered card / elevated panel */
  md: '0 4px 16px -4px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(0, 0, 0, 0.02)',
  /** Modal / overlay */
  lg: '0 8px 25px -5px rgba(0, 0, 0, 0.07), 0 4px 10px -6px rgba(0, 0, 0, 0.04)',
  /** Floating / tooltip */
  xl: '0 20px 40px -8px rgba(0, 0, 0, 0.1), 0 8px 16px -4px rgba(0, 0, 0, 0.04)',
  /** Teal accent glow — for primary action buttons */
  teal: '0 4px 14px -3px rgba(20, 145, 137, 0.3)',
  /** Inset — for inputs, well containers */
  inset: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Transitions
// ─────────────────────────────────────────────────────────────────────────────

export const transitions = {
  fast: '150ms ease-out',
  normal: '200ms ease-out',
  slow: '300ms ease-in-out',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Breakpoints (matching Tailwind)
// ─────────────────────────────────────────────────────────────────────────────

export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Utility: cn() — className merger
// ─────────────────────────────────────────────────────────────────────────────

import { twMerge } from 'tailwind-merge';
import { clsx, type ClassValue } from 'clsx';

/**
 * Merge class names with Tailwind-aware deduplication.
 * Uses clsx for conditional logic + tailwind-merge for conflict resolution.
 *
 * @example
 *   cn('px-4 py-2', isActive && 'bg-teal-600', className)
 *   cn('text-slate-700', props.className) // className overrides cleanly
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
